/**
 * Top-level render-crash boundary (Sprint 36).
 *
 * Wraps the whole app in `main.tsx`. On a render error it reports to the error
 * tracker (no-op when Sentry is off) and shows a generic, provider-neutral
 * fallback. Because it can render *after* a provider (incl. i18n) has crashed,
 * it must not use React context/hooks — so it looks strings up straight from
 * the i18n catalog instead of `useTranslation()`. Colours come from theme
 * tokens (Tailwind utilities → CSS vars), never hardcoded.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { messages } from '../../i18n/messages'
import { captureError } from '../../utils/observability'

// Standalone catalog lookup — strings still live in the i18n catalog, but we
// can't depend on the provider being mounted at crash time.
function t(key: string): string {
  return messages.en[key] ?? key
}

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error)
    // Local trail for debugging; harmless in production.
    console.error('Unhandled render error:', error, info.componentStack)
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div
        role="alert"
        className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background p-6 text-center text-foreground"
      >
        <h1 className="text-2xl font-semibold">{t('error.boundary.title')}</h1>
        <p className="max-w-md text-muted-foreground">{t('error.boundary.body')}</p>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
        >
          {t('error.boundary.reload')}
        </button>
      </div>
    )
  }
}
